import React, { useState, useEffect } from "react";
import { X, PiggyBank } from "lucide-react";

const CONTRIBUTION_TYPES = [
  { id: "INPS_VOLONTARIO", label: "INPS volontario" },
  { id: "FONDO_PENSIONE", label: "Fondo pensione" },
  { id: "ALTRO", label: "Altro" },
];

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

export default function PensionContributionModal({
  isOpen,
  onClose,
  onSave,
  contribution = null, // null = nuovo, oggetto = modifica
  darkMode = false,
}) {
  const isEdit = !!contribution;

  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState("");
  const [type, setType] = useState("INPS_VOLONTARIO");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState({});

  // Popola i campi quando si apre per modifica
  useEffect(() => {
    if (isOpen && contribution) {
      setDate(contribution.date || todayISO());
      setAmount(contribution.amount ? String(contribution.amount).replace(".", ",") : "");
      setType(contribution.type || "INPS_VOLONTARIO");
      setNotes(contribution.notes || "");
      setErrors({});
    } else if (isOpen && !contribution) {
      // Reset per nuovo
      setDate(todayISO());
      setAmount("");
      setType("INPS_VOLONTARIO");
      setNotes("");
      setErrors({});
    }
  }, [isOpen, contribution]);

  if (!isOpen) return null;

  const parseAmount = (str) => {
    if (!str) return 0;
    const cleaned = str.replace(/\s/g, "").replace(",", ".");
    return parseFloat(cleaned) || 0;
  };

  const validate = () => {
    const newErrors = {};
    const today = todayISO();

    if (!date) {
      newErrors.date = "La data è obbligatoria";
    } else if (date > today) {
      newErrors.date = "La data non può essere futura";
    }

    const numAmount = parseAmount(amount);
    if (!amount || numAmount <= 0) {
      newErrors.amount = "Inserisci un importo valido maggiore di 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;

    onSave({
      id: contribution?.id,
      date,
      amount: parseAmount(amount),
      type,
      notes: notes.trim(),
    });

    onClose();
  };

  const handleAmountChange = (e) => {
    // Permetti solo numeri, virgola e punto
    const value = e.target.value.replace(/[^0-9,.]/g, "");
    setAmount(value);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-md rounded-2xl shadow-xl ${
            darkMode ? "bg-neutral-900" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-6 py-4 border-b ${
              darkMode ? "border-neutral-800" : "border-neutral-200"
            }`}
          >
            <div className="flex items-center gap-2">
              <PiggyBank
                className={`w-5 h-5 ${
                  darkMode ? "text-forfy-300" : "text-forfy-600"
                }`}
              />
              <h2
                className={`text-lg font-semibold ${
                  darkMode ? "text-white" : "text-neutral-900"
                }`}
              >
                {isEdit ? "Modifica Versamento" : "Nuovo Versamento"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-lg transition-colors ${
                darkMode
                  ? "hover:bg-neutral-800 text-neutral-500"
                  : "hover:bg-neutral-100 text-neutral-500"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Data */}
            <div>
              <label
                htmlFor="contributionDate"
                className={`block text-sm font-medium mb-1.5 ${
                  darkMode ? "text-neutral-400" : "text-neutral-700"
                }`}
              >
                Data versamento <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="contributionDate"
                value={date}
                max={todayISO()}
                onChange={(e) => setDate(e.target.value)}
                className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  errors.date
                    ? darkMode
                      ? "border-red-700 bg-red-900/30"
                      : "border-red-300 bg-red-50"
                    : darkMode
                    ? "border-neutral-700 bg-neutral-800 text-white"
                    : "border-neutral-300 bg-white text-neutral-900"
                }`}
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-500">{errors.date}</p>
              )}
            </div>

            {/* Importo */}
            <div>
              <label
                htmlFor="contributionAmount"
                className={`block text-sm font-medium mb-1.5 ${
                  darkMode ? "text-neutral-400" : "text-neutral-700"
                }`}
              >
                Importo <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span
                  className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                    darkMode ? "text-neutral-500" : "text-neutral-500"
                  }`}
                >
                  €
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  id="contributionAmount"
                  value={amount}
                  onChange={handleAmountChange}
                  placeholder="0,00"
                  className={`w-full pl-8 pr-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.amount
                      ? darkMode
                        ? "border-red-700 bg-red-900/30"
                        : "border-red-300 bg-red-50"
                      : darkMode
                      ? "border-neutral-700 bg-neutral-800 text-white"
                      : "border-neutral-300 bg-white text-neutral-900"
                  }`}
                />
              </div>
              {errors.amount && (
                <p className="mt-1 text-sm text-red-500">{errors.amount}</p>
              )}
            </div>

            {/* Tipo */}
            <div>
              <label
                htmlFor="contributionType"
                className={`block text-sm font-medium mb-1.5 ${
                  darkMode ? "text-neutral-400" : "text-neutral-700"
                }`}
              >
                Tipo di versamento
              </label>
              <select
                id="contributionType"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  darkMode
                    ? "border-neutral-700 bg-neutral-800 text-white"
                    : "border-neutral-300 bg-white text-neutral-900"
                }`}
              >
                {CONTRIBUTION_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Note */}
            <div>
              <label
                htmlFor="contributionNotes"
                className={`block text-sm font-medium mb-1.5 ${
                  darkMode ? "text-neutral-400" : "text-neutral-700"
                }`}
              >
                Note (opzionale)
              </label>
              <textarea
                id="contributionNotes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Es: versamento trimestrale..."
                className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  darkMode
                    ? "border-neutral-700 bg-neutral-800 text-white placeholder:text-neutral-600"
                    : "border-neutral-300 bg-white text-neutral-900 placeholder:text-neutral-400"
                }`}
              />
            </div>

            {/* Footer */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                  darkMode
                    ? "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                    : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                }`}
              >
                Annulla
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2.5 rounded-lg font-medium text-sm bg-forfy-500 text-white hover:bg-forfy-600 transition-colors"
              >
                {isEdit ? "Salva modifiche" : "Aggiungi"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
